export const PRODUCTION_WORK_STATUS_CODES = Object.freeze([
    'SW', 'W', 'WF', 'H', 'SWF',
] as const);

export type ActiveProductionWorkStatus = typeof PRODUCTION_WORK_STATUS_CODES[number];
export type ProductionWorkStatus = '' | ActiveProductionWorkStatus;

export interface ProductionWorkStatusPresentation {
    code: ActiveProductionWorkStatus;
    doodLabel: string;
    doodBadge: Readonly<{
        background: string;
        text: string;
        border: string;
    }>;
    callSheetDescription: string;
    callSheetColor: string;
}

export const PRODUCTION_WORK_STATUS_PRESENTATIONS = Object.freeze([
    {
        code: 'SW',
        doodLabel: 'Start',
        doodBadge: Object.freeze({
            background: 'rgba(52,211,153,0.18)',
            text: '#6EE7B7',
            border: 'rgba(52,211,153,0.3)',
        }),
        callSheetDescription: 'Start Work',
        callSheetColor: '#34D399',
    },
    {
        code: 'W',
        doodLabel: 'Work',
        doodBadge: Object.freeze({
            background: 'rgba(167,139,250,0.18)',
            text: '#C4B5FD',
            border: 'rgba(167,139,250,0.3)',
        }),
        callSheetDescription: 'Work',
        callSheetColor: '#60A5FA',
    },
    {
        code: 'WF',
        doodLabel: 'Finish',
        doodBadge: Object.freeze({
            background: 'rgba(251,146,60,0.18)',
            text: '#FDBA74',
            border: 'rgba(251,146,60,0.3)',
        }),
        callSheetDescription: 'Work Finish',
        callSheetColor: '#F472B6',
    },
    {
        code: 'H',
        doodLabel: 'Hold',
        doodBadge: Object.freeze({
            background: 'rgba(161,161,170,0.06)',
            text: '#71717A',
            border: 'rgba(161,161,170,0.12)',
        }),
        callSheetDescription: 'Hold',
        callSheetColor: '#FBBF24',
    },
    {
        code: 'SWF',
        doodLabel: 'Start & Finish',
        doodBadge: Object.freeze({
            background: 'rgba(34,211,238,0.18)',
            text: '#67E8F9',
            border: 'rgba(34,211,238,0.3)',
        }),
        callSheetDescription: 'Start/Finish',
        callSheetColor: '#A78BFA',
    },
] as const satisfies readonly ProductionWorkStatusPresentation[]);

const PRESENTATION_BY_CODE = new Map<ActiveProductionWorkStatus, ProductionWorkStatusPresentation>(
    PRODUCTION_WORK_STATUS_PRESENTATIONS.map(presentation => [presentation.code, presentation]),
);

export const getProductionWorkStatusPresentation = (
    status: string,
): Readonly<ProductionWorkStatusPresentation> | undefined =>
    PRESENTATION_BY_CODE.get(status as ActiveProductionWorkStatus);

/** DOOD legend order remains SW, W, WF, SWF, H. */
export const DOOD_WORK_STATUS_PRESENTATIONS = Object.freeze(
    (['SW', 'W', 'WF', 'SWF', 'H'] as const).map(code => PRESENTATION_BY_CODE.get(code)!),
);

export interface CallSheetWorkStatusOption {
    value: ProductionWorkStatus;
    label: string;
    description?: string;
    color?: string;
}

/** Call Sheet select order remains blank, SW, W, WF, H, SWF. */
export const CALL_SHEET_WORK_STATUS_OPTIONS: readonly CallSheetWorkStatusOption[] = Object.freeze([
    Object.freeze({ value: '', label: '—' }),
    ...PRODUCTION_WORK_STATUS_CODES.map(code => {
        const presentation = PRESENTATION_BY_CODE.get(code)!;
        return Object.freeze({
            value: code,
            label: code,
            description: presentation.callSheetDescription,
            color: presentation.callSheetColor,
        });
    }),
]);
